import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

// Components
import { Container, Col, Row } from 'reactstrap';
import Feature from 'components/atoms/Feature/';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader/';
import Alert from 'components/atoms/Alert';
import Modal from 'components/atoms/Modal';
import PackageList from 'components/atoms/PackageList';
import PackagesModal from 'components/molecules/PackagesModal';
import ServiceTextBox from 'components/atoms/ServiceTextBox';
import ServicesList from 'components/molecules/ServicesList';
import RecallOptions from 'components/molecules/RecallOptions';
import Button from 'components/atoms/Button';
import LoaderHOC from 'components/atoms/LoaderHOC';
import MileageSelector from 'components/molecules/MileageSelector';
import { Icon } from 'components/atoms/Icon/';

// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Utils
import {
  HOME_ROUTE,
  USER_VEHICLES_ROUTE,
  VEHICLE_ROUTE,
  RECALL_SERVICES_ROUTE,
  INDIVIDUAL_SERVICES_ROUTE,
  DECLINED_SERVICES_ROUTE,
  TRANSPORTATION_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicator } from 'constants/CTAConfig';
import { isTrue, undefinedToTrue } from 'utils/varValidation';
import { isEmpty, getMMLabel, getCTAbackButtonLabel } from 'utils/string';
import { isMobile, resetScrollPosition } from 'utils/DOM';

import ServicesPageLoader from './ServicesPageLoader';
import './ServicesPage.scss';

const ns = 'services-page';
const { FORWARD } = NAVIGATION;

class ServicesPage extends Component {
  constructor(props) {
    super(props);
    this.selectActivePackage = this.selectActivePackage.bind(this);
    this.modal = React.createRef();
    this.state = {
      activePackage: null
    };
  }

  componentDidMount() {
    resetScrollPosition(0);
    const { disableAlert, cleanAdvisorError } = this.props;
    disableAlert();
    cleanAdvisorError();
  }

  selectActivePackage(activePackage) {
    this.setState({ activePackage }, () => {
      this.modal.current.setShowModal(true);
    });
  }

  buildModal(packageList, selectedPackage) {
    const { intl } = this.props;
    const modalNS = 'packages-modal';
    return (
      <Modal
        ref={this.modal}
        size={'large'}
        showCloseButton={false}
        className={modalNS}
        footer={() => (
          <div className={`${modalNS}__footer d-none`}>
            <div className={`${modalNS}__price`} />
            <Button
              buttonTheme="link"
              className={`${modalNS}__footer__continue-button text-right`}
              disabled={true}
              onClick={() => {}}
              aria-label={intl.formatMessage({
                id: 'c8.aria.add.package',
                defaultMessage: 'Add Package'
              })}
            >
              {intl.formatMessage({
                id: 'c8.services.page.packages.modal.button.add'
              })}
            </Button>
          </div>
        )}
      >
        <PackagesModal
          packages={packageList}
          selectedPackage={selectedPackage}
          closeModal={() => this.modal.current.setShowModal(false)}
          activePackage={this.state.activePackage}
        />
      </Modal>
    );
  }

  packageListComponent = (packageList, selectedPackage) => {
    // @TODO: packages should also be rendered if revisited appt has packages
    // (use displayPackages selector)
    // at the moment app is using same logic as CP7
    const { intl } = this.props;
    return (
      packageList.length > 0 && (
        <div
          className={`${ns}__service`}
          id="maintenance_package_section"
          tabIndex={0}
          role={'group'}
          aria-label={intl.formatMessage({
            id: 'c8.aria.select.a.maintenance.package',
            defaultMessage: 'Select a maintenance package'
          })}
        >
          <div>
            <MileageSelector
              mileageLabel={intl.formatMessage({
                id: 'c8.vehicle.page.mileage'
              })}
            />
            {this.buildModal(packageList, selectedPackage)}
            <PackageList
              packagesList={packageList}
              selectPackage={this.selectActivePackage}
            />
          </div>
        </div>
      )
    );
  };

  serviceListComponent = displayServices => {
    const { intl } = this.props;
    return (
      <div
        id="individual_services_section"
        tabIndex={0}
        role={'group'}
        aria-label={intl.formatMessage({
          id: 'c8.aria.select.individual.services',
          defaultMessage: 'Select individual services'
        })}
      >
        {displayServices &&
          displayServices.length > 0 && (
            <div>
              <div className={`${ns}__label`}>
                {intl.formatMessage({
                  id: 'c8.services.page.individual.services.list.label'
                })}
              </div>
              <ServicesList
                serviceList={displayServices}
                group={'mostCommon'}
              />
              <Row
                className={`${ns}__additional-details d-flex no-gutters m-0`}
                onClick={() =>
                  this.props.goTo(INDIVIDUAL_SERVICES_ROUTE, FORWARD)
                }
              >
                <Col xs="1" className={`${ns}__aligner`} />
                <Col
                  xs="11"
                  className={`ml-n2`}
                  aria-label={intl.formatMessage({
                    id: 'c8.aria.view.all.individual.services.and.repairs',
                    defaultMessage: 'View all individual services and repairs'
                  })}
                >
                  <Button
                    className={'button--light'}
                    buttonTheme={'link'}
                    id="all_services_repair_button"
                  >
                    {intl.formatMessage({
                      id:
                        'c8.services.page.individual.services.list.button.all.services'
                    })}
                  </Button>
                </Col>
              </Row>
            </div>
          )}
      </div>
    );
  };

  getPreviousRoute = () => {
    const {
      declinedServicesExist,
      showMileage,
      vehicleEditableEnabled,
      isLoggedIn
    } = this.props;
    if (declinedServicesExist) {
      return DECLINED_SERVICES_ROUTE;
    }
    if (!showMileage) {
      if (isLoggedIn) {
        return USER_VEHICLES_ROUTE;
      }
      if (!vehicleEditableEnabled && isMobile()) {
        return HOME_ROUTE;
      }
    }
    return VEHICLE_ROUTE;
  };

  render() {
    const {
      className,
      servicesCount,
      servicePoint,
      model,
      individualServices,
      recallServices,
      packageList,
      gtmUserEventTellUsMoreSelected,
      goTo,
      policies,
      declinedServicesExist,
      appointment,
      promotion,
      promotionGroup,
      hasPromotion,
      intl,
      servicesFormOptimizely,
      modelLabelOther,
      ctaBanner,
      gtmUserEvent,
      vehicle,
      showProgressBar,
      hideBottomCTA,
      ctaBackButton
    } = this.props;
    const customMessagingBannerLabel = intl.formatMessage({
      id: 'c8.services.page.entry.custom.messaging.services'
    });
    const selectedPackage = servicePoint
      ? servicePoint.menuType.description
      : null;
    const classProps = cn(ns, className);
    const amountToShowServicesFromOptimizely = servicesFormOptimizely
      ? servicesFormOptimizely
      : 2;
    const amountToShowServices =
      packageList.length > 0 ? amountToShowServicesFromOptimizely : 6;
    const displayServices = individualServices.slice(0, amountToShowServices);
    const onBackButton = this.getPreviousRoute();
    const modelLabel = getMMLabel(model, modelLabelOther, intl);
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.select.vehicle',
      defaultMessage: 'Back to select vehicle'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.setting.up.your.appointment',
      defaultMessage: 'Continue setting up your appointment'
    });
    const label = intl.formatMessage({
      id: declinedServicesExist
        ? 'c8.header.declined.services'
        : 'c8.header.vehicle'
    });
    const { make, year } = vehicle;
    const headerVehicleLabel = `${make} · ${year} · ${model}`;
    const ctaLabel = getCTAbackButtonLabel(
      ctaBackButton,
      headerVehicleLabel,
      label,
      intl
    );

    return (
      <AppointmentFlowWizard
        className={classProps}
        hideFooter={Boolean(hideBottomCTA)}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              label={ctaLabel}
              hideServices={false}
              routeTo={onBackButton}
              backButtonAriaLabel={backButtonAriaLabel}
              ctaButtonDisabled={servicesCount < 1}
              ctaBanner={ctaBanner}
              onClickCtaButton={() => {
                gtmUserEventTellUsMoreSelected();
                gtmUserEvent(
                  'NEXT button',
                  'Clicked',
                  `Go to next page through CTA`,
                  `GTM_CURRENT_PAGE > ${TRANSPORTATION_ROUTE} page`
                );
                goTo(TRANSPORTATION_ROUTE, FORWARD);
              }}
              typeButton={'cta-next'}
              nextButtonAriaLabel={nextButtonAriaLabel}
            />
          ) : (
            <AppointmentFlowHeader
              label={label}
              routeTo={onBackButton}
              backButtonAriaLabel={backButtonAriaLabel}
            />
          );
        }}
        footer={() => {
          return !hideBottomCTA ? (
            <AppointmentFlowFooter
              buttonDisabled={servicesCount < 1}
              buttonText={intl.formatMessage({
                id: 'c8.footer.next'
              })}
              onClick={() => {
                gtmUserEventTellUsMoreSelected();
                goTo(TRANSPORTATION_ROUTE, FORWARD);
              }}
              ariaLabel={nextButtonAriaLabel}
            />
          ) : null;
        }}
        body={() => (
          <React.Fragment>
            {showProgressBar && (
              <CtaStepIndicator
                htmlId="VehiclePageStepIndicator"
                labels={labelsStepIndicator(intl)}
                activeStep={2}
              />
            )}
            <Container role={'list'}>
              {appointment.startTime && (
                <Alert color="warning" animation="top-down">
                  {intl.formatMessage({
                    id: 'c8.alert.add.remove.services.may.affect.availability'
                  })}
                </Alert>
              )}
              {hasPromotion &&
                promotionGroup === 'service' && (
                  <Alert color="warning" animation="top-down">
                    {intl.formatMessage(
                      { id: 'c8.alert.promotion.applied' },
                      { promotion: promotion.promotionDescription }
                    )}
                  </Alert>
                )}
              {!isEmpty(customMessagingBannerLabel) && (
                <div className={`${ns}__banner-info mb-3 d-flex flex-row`}>
                  <div
                    className={`${ns}__icon-container d-flex align-items-center px-1 px-lg-2 py-2`}
                  >
                    <Icon
                      type="light-bulb"
                      className={`${ns}__icon`}
                      id="light_bulb_icon"
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.message.from.dealership',
                        defaultMessage: 'Message from dealership'
                      })}
                    />
                  </div>
                  <div
                    className={`${ns}__recommended-subtitle px-1 px-lg-2 py-2`}
                  >
                    {customMessagingBannerLabel}
                  </div>
                </div>
              )}
              <h1 className={`${ns}__title`}>
                {intl.formatMessage(
                  { id: 'c8.services.page.title' },
                  { vehicleName: modelLabel }
                )}
              </h1>
              {recallServices &&
                recallServices.length > 0 && (
                  <Alert
                    color="danger"
                    icon="error_outline"
                    onClick={() => goTo(RECALL_SERVICES_ROUTE, FORWARD)}
                  >
                    {intl.formatMessage({
                      id: 'c8.alert.has.active.recall'
                    })}
                  </Alert>
                )}
              {isTrue(policies.recommendationAllowed) && (
                <Feature
                  name="$servicePackages"
                  render={() => (
                    <LoaderHOC
                      serviceId={'packages'}
                      placeHolderType={() => <ServicesPageLoader />}
                      component={() =>
                        this.packageListComponent(packageList, selectedPackage)
                      }
                    />
                  )}
                />
              )}
              {(isTrue(undefinedToTrue(policies.maintenanceSvcVisible)) ||
                isTrue(undefinedToTrue(policies.repairSvcVisible))) && (
                <LoaderHOC
                  serviceId={'maintenance|repair|metaVehicle'}
                  placeHolderType={() => <ServicesPageLoader />}
                  component={() => this.serviceListComponent(displayServices)}
                />
              )}
              <RecallOptions />
              <ServiceTextBox />
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

ServicesPage.propTypes = {
  addService: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  className: PropTypes.string,
  declinedServicesExist: PropTypes.bool.isRequired,
  disableAlert: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired,
  gtmUserEventTellUsMoreSelected: PropTypes.func.isRequired,
  hasPromotion: PropTypes.bool,
  individualServices: PropTypes.array.isRequired,
  intl: PropTypes.object.isRequired,
  isLoggedIn: PropTypes.bool,
  isValetAvailable: PropTypes.bool,
  model: PropTypes.string,
  packageList: PropTypes.array,
  policies: PropTypes.object.isRequired,
  promotion: PropTypes.object,
  promotionGroup: PropTypes.string,
  recallServices: PropTypes.array.isRequired,
  removeService: PropTypes.func.isRequired,
  servicePoint: PropTypes.object,
  servicesCount: PropTypes.number.isRequired,
  showMileage: PropTypes.bool,
  vehicleEditableEnabled: PropTypes.bool,
  servicesFormOptimizely: PropTypes.number,
  modelLabelOther: PropTypes.string,
  ctaBanner: PropTypes.object,
  gtmUserEvent: PropTypes.func,
  vehicle: PropTypes.object,
  showProgressBar: PropTypes.bool,
  hideBottomCTA: PropTypes.bool,
  ctaBackButton: PropTypes.string,
  cleanAdvisorError: PropTypes.func.isRequired
};

export default ServicesPage;



// WEBPACK FOOTER //
// ./src/components/pages/ServicesPage/ServicesPage.js